import useUser from '../../../contexts/hooks/useUser'
import { trpc } from '../../../modules/trpc'
import { defaultScoreboardData } from '../constants/scoreboardConstants'

export type ScoreboardHook = ReturnType<typeof useScoreboard>

export const useScoreboard = () => {
	const { user } = useUser()

	const {
		data: totalPoints,
		isLoading: isLoadingPoints,
		error: pointsError,
	} = trpc.achievements.getUserTotalPoints.useQuery(
		{ userId: user?.id ?? 0 },
		{
			enabled: !!user,
			staleTime: 5000 * 60, // 5 min
		}
	)

	const {
		data: totalSavings,
		isLoading: isLoadingSavings,
		error: savingsError,
	} = trpc.achievements.getUserTotalSavings.useQuery(
		{ userId: user?.id ?? 0 },
		{
			enabled: !!user,
			staleTime: 5000 * 60, // 5 min
		}
	)

	const {
		data: lastLevel,
		isLoading: isLoadingLevel,
		error: levelError,
	} = trpc.achievements.getUserLastLevel.useQuery(
		{ userId: user?.id ?? 0 },
		{
			enabled: !!user,
			staleTime: 5000 * 60, // 5 min
		}
	)

	const {
		data: lastPercentage,
		isLoading: isLoadingPercentage,
		error: percentageError,
	} = trpc.achievements.getUserLastPercentage.useQuery(
		{ userId: user?.id ?? 0 },
		{
			enabled: !!user,
			staleTime: 5000 * 60, // 5 min
		}
	)

	const {
		data: lastPoints,
		isLoading: isLoadingLastPoints,
		error: lastPointsError,
	} = trpc.achievements.getUserLastPoints.useQuery(
		{ userId: user?.id ?? 0 },
		{
			enabled: !!user,
			staleTime: 5000 * 60, // 5 min
		}
	)

	const {
		data: lastSavings,
		isLoading: isLoadingLastSavings,
		error: lastSavingsError,
	} = trpc.achievements.getUserLastSavings.useQuery(
		{ userId: user?.id ?? 0 },
		{
			enabled: !!user,
			staleTime: 5000 * 60, // 5 min
		}
	)

	const {
		data: bestSimilarHouseholds,
		isLoading: isLoadingHouseholds,
		error: householdsError,
	} = trpc.achievements.getBestSimilarHouseholds.useQuery(
		{ userId: user?.id ?? 0 },
		{
			enabled: !!user,
			staleTime: 5000 * 60, // 5 min
		}
	)

	const isLoading =
		isLoadingPoints ||
		isLoadingSavings ||
		isLoadingLevel ||
		isLoadingPercentage ||
		isLoadingLastPoints ||
		isLoadingLastSavings ||
		isLoadingHouseholds
	const error =
		pointsError ||
		savingsError ||
		levelError ||
		percentageError ||
		lastPointsError ||
		lastSavingsError ||
		householdsError

	if (!user || error) {
		return {
			...defaultScoreboardData,
			error: error?.message || null,
		}
	}

	if (
		isLoading ||
		!totalPoints ||
		!lastLevel ||
		!totalSavings ||
		!lastPercentage ||
		!lastPoints ||
		!lastSavings ||
		!bestSimilarHouseholds
	) {
		return {
			...defaultScoreboardData,
			isLoading,
		}
	}

	return {
		totalPoints: totalPoints.result,
		totalSavings: totalSavings.result,
		lastPoints: lastPoints.result,
		lastSavings: lastSavings.result,
		lastLevel: lastLevel.result,
		lastPercentage: lastPercentage.result,
		bestSimilarHouseholds: bestSimilarHouseholds.result,
		isLoading,
		error: null,
	}
}
